import { SxProps } from '@mui/material';
import { canAccess, Permissions } from '@react-admin/ra-rbac';
import React, { FC, useContext } from 'react';
import {
  TabbedShowLayout,
  TabbedShowLayoutProps,
  usePermissions,
  useSidebarState,
} from 'react-admin';

import { RightPanelContext, TabbedShowLayoutSearchTabs } from '@sh4res/bo-common/components';

const tabsStyle: SxProps = {
  position: 'fixed',
  padding: '8px 18px 0px 0px',
  width: '200px',
  '& .MuiTab-textColorPrimary': {
    color: '#999999',
  },
  '& .MuiTouchRipple-child': {
    backgroundColor: '#BBBBBB !important',
  },
  '& .MuiTabs-scroller': {
    overflow: 'auto !important',
    maxHeight: 'calc(var(--app-height) - 60px - 24px)',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
};

const tabSelectedStyles: SxProps = {
  backgroundColor: '#16002C08',
  width: '100%',
  zIndex: '-1',
  borderRadius: '6px',
};

export const VerticalTabsLayout: FC<TabbedShowLayoutProps> = ({ children, ...props }) => {
  const [isSidebarOpen] = useSidebarState();
  const { isOpen: isRightPanelOpen } = useContext(RightPanelContext);
  const { permissions } = usePermissions<Permissions | undefined>();

  let margin = '60px';
  if (isSidebarOpen) {
    margin = '260px';
  } else if (isRightPanelOpen) {
    margin = '475px';
  }

  const filteredChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) {
      return child;
    }
    const { childPermission } = child.props as {
      childPermission?: { resource: string; action: string };
    };

    // allow by default
    if (!childPermission) {
      return child;
    }

    if (!permissions) {
      return null;
    }
    return canAccess({
      permissions,
      resource: childPermission.resource,
      action: childPermission.action,
    })
      ? child
      : null;
  });

  return (
    <TabbedShowLayout
      {...props}
      syncWithLocation={false}
      sx={{
        display: 'flex',
        height: '100%',
        '>.RaTabbedShowLayout-content': {
          maxWidth: `calc(100vw - ${margin})`,
          width: '100%',
          padding: '0px 0px 10px 200px',
          display: 'flex',
          '& .MuiStack-root': {
            height: '100%',
            width: '100%',
          },
        },
      }}
      tabs={
        <TabbedShowLayoutSearchTabs
          sx={tabsStyle}
          variant="standard"
          orientation="vertical"
          TabIndicatorProps={{
            sx: tabSelectedStyles,
          }}
          selectionFollowsFocus={false}
        />
      }>
      {filteredChildren}
    </TabbedShowLayout>
  );
};
